// app/frontend/components/ui/AnnouncementBanner.tsx
import { Box, Text } from "@mantine/core";
import React from "react";

export interface AnnouncementBannerProps {
  /**
   * Main title/heading text for the banner
   */
  title: string;
  /**
   * Description/subtitle text displayed below the title
   */
  description: string;
  /**
   * Optional maximum width for the banner (default: "700px")
   */
  maxWidth?: string;
  /**
   * Optional margin bottom (default: "clamp(24px, 6vw, 48px)")
   */
  marginBottom?: string;
  /**
   * Optional custom styles for the banner container
   */
  style?: React.CSSProperties;
}

/**
 * AnnouncementBanner - Reusable announcement/alert banner component
 * 
 * Features:
 * - Reduced visual weight with subtle gradient background
 * - Hover effects for interactivity
 * - Responsive typography and spacing
 * - Inline with content design
 * 
 * @example
 * <AnnouncementBanner
 *   title="Learntokiz is currently being rebuilt into version 2.0 ✨"
 *   description="Stay tuned for a fresh learning experience, new features, and improved community tools!"
 * />
 */
export function AnnouncementBanner({
  title,
  description,
  maxWidth = "700px",
  marginBottom = "clamp(24px, 6vw, 48px)",
  style,
}: AnnouncementBannerProps) {
  return (
    <Box
      style={{
        width: "100%",
        maxWidth: maxWidth,
        margin: `0 auto ${marginBottom} auto`,
        textAlign: "center",
        padding: "clamp(12px, 3vw, 16px) clamp(16px, 4vw, 20px)",
        background:
          "linear-gradient(135deg, rgba(255,152,0,0.15), rgba(255,193,7,0.12))",
        border: "1px solid rgba(255, 152, 0, 0.2)",
        borderRadius: "12px",
        backdropFilter: "blur(8px)",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        transition: "all 0.3s ease",
        ...style,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "rgba(255, 152, 0, 0.3)";
        e.currentTarget.style.boxShadow = "0 4px 12px rgba(255, 152, 0, 0.15)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "rgba(255, 152, 0, 0.2)";
        e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.1)";
      }}
    >
      <Text
        fw={700}
        style={{
          color: "#FFD580",
          letterSpacing: "0.3px",
          marginBottom: "4px",
          fontSize: "clamp(0.875rem, 3.5vw, 1rem)",
          lineHeight: 1.4,
        }}
      >
        {title}
      </Text>
      <Text
        size="sm"
        c="rgba(255,255,255,0.75)"
        style={{
          fontSize: "clamp(0.75rem, 3vw, 0.875rem)",
          lineHeight: 1.5,
        }}
      >
        {description}
      </Text>
    </Box>
  );
}

